import './edit-teams.scss';
import { inject } from 'aurelia';
import { IRouter } from '@aurelia/router';
import { TeamSettingEntity } from '../../apis/team/team-setting-entity';
import { TeamSettingViewModel, TeamSettingListViewModel } from './team-setting-view-model';
import { I18N } from '@aurelia/i18n';
import { MessageType } from '../../components/callout/message-type';
import { Message } from '../../components/callout/message';
import { ProjectModelService } from '../../services/project-model-service';
import { GroupEntity } from '../../apis/group/group-entity';

@inject(IRouter, I18N, ProjectModelService)
export class EditTeams {

    public teamSettingsList: TeamSettingListViewModel[] = [];
    public messages: Message[] = [];
    public selectedGroupId: string = '';
    public newAssociationName: string = '';
    public newAssociationInput: HTMLInputElement;
    // Associations present when the page loaded, keyed by name, holding the server associationId
    // when known (null for associations not yet persisted).
    private _originalAssociationNames: Map<string, string | null> = new Map();
    private _eventId: string = null;
    private _groups: GroupEntity[] = [];

    private _router: IRouter = null;
    private _i18n: I18N = null;
    private _projectModelService: ProjectModelService;

    constructor(router: IRouter, i18n: I18N, projectModelService: ProjectModelService) {
        this._router = router;
        this._i18n = i18n;
        this._projectModelService = projectModelService;
    }

    public async loading(params: any): Promise<void> {
        this._eventId = params.eventId;
        if (!this._eventId) {
            void this._router.load('create');
            return;
        }

        const model = this._projectModelService.currentModel
            ?? await this._projectModelService.loadByEventId(this._eventId, false);
        this._groups = model?.groups ?? [];
        if (this._groups.length === 0) {
            void this._router.load('create');
            return;
        }
        const groupIdParam: string = params.groupId ?? null;
        this.selectedGroupId = groupIdParam ?? this._groups[0]?.id ?? '';
        this._loadTeamsForGroup();
    }

    public get currentGroupName(): string {
        const group = this._groups.find(g => g.id === this.selectedGroupId);
        return group?.name ?? '';
    }

    public get pageTitle(): string {
        const base = this._i18n.tr('ui:attending_teams');
        return this.currentGroupName
            ? `${base} - ${this.currentGroupName}`
            : base;
    }

    public get associationFallbackText(): string {
        return this._i18n.tr('ui:association');
    }

    public get expandTitle(): string {
        return this._i18n.tr('ui:expand');
    }

    public get collapseTitle(): string {
        return this._i18n.tr('ui:collapse');
    }

    private _loadTeamsForGroup(): void {
        this.teamSettingsList.clear();
        this._originalAssociationNames.clear();
        if (!this.selectedGroupId) { return; }
        const teams = this._projectModelService
            .getTeamsForGroups([this.selectedGroupId])
            .map(t => TeamSettingEntity.fromDto(t.toDto()));
        const teamsByAssociation = new Map(teams.groupBy(t => t.association, t => t).map(g => [g.key, g.values]));

        // Start from every association the backend knows about (name + id), even ones with no
        // remaining teams, then union in any not-yet-saved associations that only exist on teams.
        const knownAssociations = this._projectModelService.getAssociationsForGroup(this.selectedGroupId);
        const associationIdsByName = new Map(knownAssociations.map(a => [a.name, a.associationId]));
        const associationNames = Array.from(new Set([...associationIdsByName.keys(), ...teamsByAssociation.keys()]))
            .sort((a, b) => a.localeCompare(b));

        for (const associationName of associationNames) {
            const list = new TeamSettingListViewModel(associationName, teamsByAssociation.get(associationName) ?? []);
            list.teamSettings.forEach(t => { t.originalName = t.toEntity().name; });
            list.contacts = this._projectModelService.getAssociationContacts(this.selectedGroupId, associationName);
            this.teamSettingsList.add(list);
            list.originalAssociation = associationName;
            this._originalAssociationNames.set(associationName, associationIdsByName.get(associationName) ?? null);
        }
        this._updateInfoMessages();
    }

    // "demoX:Y" generates X associations (A, B, C, ...) with Y teams each (suffix 1..Y).
    private _parseDemo(): { associations: number; teams: number } | null {
        const match = /^demo(\d+):(\d+)$/i.exec(this.newAssociationName?.trim() ?? '');
        if (!match) { return null; }
        const associations = parseInt(match[1], 10);
        const teams = parseInt(match[2], 10);
        if (associations < 1 || teams < 1) { return null; }
        return { associations, teams };
    }

    private static _associationLetters(index: number): string {
        let name = '';
        for (let n = index + 1; n > 0; n = Math.floor((n - 1) / 26)) {
            name = String.fromCharCode(65 + ((n - 1) % 26)) + name;
        }
        return name;
    }

    private _teamNameCounts(): Map<string, number> {
        const counts = new Map<string, number>();
        for (const list of this.teamSettingsList) {
            for (const team of list.teamSettings) {
                const name = team.toEntity().name;
                counts.set(name, (counts.get(name) ?? 0) + 1);
            }
        }
        return counts;
    }

    // The schedule stores teams by display name, so two teams in a group must never share one (e.g. "A"+"1" and "A 1").
    public hasTeamNameCollision(teamSetting: TeamSettingViewModel): boolean {
        return (this._teamNameCounts().get(teamSetting.toEntity().name) ?? 0) > 1;
    }

    public listHasTeamNameCollision(list: TeamSettingListViewModel): boolean {
        const counts = this._teamNameCounts();
        return list.teamSettings.some(t => (counts.get(t.toEntity().name) ?? 0) > 1);
    }

    public get hasAnyTeamNameCollision(): boolean {
        return Array.from(this._teamNameCounts().values()).some(count => count > 1);
    }

    private _addDemoAssociations(associations: number, teams: number): void {
        const usedNames = new Set(this.teamSettingsList.map(ts => ts.association));
        const teamNames = new Set(this._teamNameCounts().keys());
        const lists: TeamSettingListViewModel[] = [];
        for (let letterIndex = 0; lists.length < associations; letterIndex++) {
            const name = EditTeams._associationLetters(letterIndex);
            if (usedNames.has(name)) { continue; }
            if (Array.from({ length: teams }, (_, i) => `${name} ${i + 1}`).some(n => teamNames.has(n))) { continue; }
            const list = new TeamSettingListViewModel(name, []);
            for (let i = 1; i <= teams; i++) {
                list.addTeam(new TeamSettingEntity(name, i.toString()));
            }
            lists.push(list);
        }
        this.teamSettingsList.unshift(...lists);
    }

    public get canAddAssociation(): boolean {
        const trimmed = this.newAssociationName?.trim();
        if (!trimmed) { return false; }
        const demo = this._parseDemo();
        if (demo) {
            return this.currentTeamCount + demo.associations * demo.teams <= 120;
        }
        if (this.teamSettingsList.some(ts => ts.association === trimmed)) { return false; }
        if (this._teamNameCounts().has(trimmed)) { return false; }
        return !this.maxTeamsCountReached;
    }

    // Creates a new association with a single, empty-suffix team so the user can immediately
    // rename/add teams inline instead of having to fill in a separate "team" step.
    public confirmNewAssociation(): void {
        if (!this.canAddAssociation) { return; }

        const demo = this._parseDemo();
        if (demo) {
            this._addDemoAssociations(demo.associations, demo.teams);
        } else {
            const trimmed = this.newAssociationName.trim();
            const list = new TeamSettingListViewModel(trimmed, []);
            list.addTeam();
            list.isExpanded = true;
            this.teamSettingsList.unshift(list);
        }

        this.newAssociationName = '';
        this._updateInfoMessages();
        setTimeout(() => this.newAssociationInput?.focus(), 0);
    }

    // Old name is read before the two-way binding on the caller's inline-edit-text overwrites it.
    public renameAssociation(list: TeamSettingListViewModel, newName: string): void {
        const oldName = list.association;
        const trimmed = (newName ?? '').trim();
        if (!trimmed || trimmed === oldName) { return; }

        list.association = trimmed;
        this._updateInfoMessages();
    }

    public isDuplicateAssociationName(list: TeamSettingListViewModel): boolean {
        return this.teamSettingsList.some(other => other !== list && other.association === list.association);
    }

    public addTeamToAssociation(list: TeamSettingListViewModel): void {
        list.addTeam();
        this._updateInfoMessages();
    }

    public removeTeamFromAssociation(list: TeamSettingListViewModel, teamSetting: TeamSettingViewModel): void {
        list.removeTeamSetting(teamSetting.toEntity());
        // The association card stays visible even with 0 teams left — it may still have contacts
        // that need managing, and must be removed explicitly via removeAssociation().
        this._updateInfoMessages();
    }

    public addContact(list: TeamSettingListViewModel): void {
        list.contacts.push({ email: '' });
    }

    public removeContact(list: TeamSettingListViewModel, index: number): void {
        list.contacts.splice(index, 1);
    }

    public removeAssociation(list: TeamSettingListViewModel): void {
        this.teamSettingsList.remove(list);
        this._originalAssociationNames.delete(list.association);
        this._projectModelService.deleteAssociation(this.selectedGroupId, list.association);
        this._updateInfoMessages();
    }

    public back(): void {
        if (this.hasAnyTeamNameCollision) { return; }
        this._saveTeamsForCurrentGroup();
        void this._router.load(`create/event-summary/${this._eventId}`);
    }

    public cancel(): void {
        void this._router.load(`create/event-summary/${this._eventId}`);
    }

    private _saveTeamsForCurrentGroup(): void {
        if (!this.selectedGroupId) { return; }
        const allTeamViewModels = this.teamSettingsList.selectMany(tsl => tsl.teamSettings);
        const allTeams: TeamSettingEntity[] = allTeamViewModels.map(t => {
            const entity = t.toEntity();
            entity.groupId = this.selectedGroupId;
            return entity;
        });
        this._projectModelService.saveTeamsForGroup(this.selectedGroupId, allTeams);

        // Schedule rows reference teams by display name, so follow renamed teams (association or suffix).
        const teamRenames = allTeamViewModels
            .filter(t => t.originalName != null && t.originalName !== t.toEntity().name)
            .map(t => ({ from: t.originalName, to: t.toEntity().name }));
        this._projectModelService.renameTeamsInSchedules(this.selectedGroupId, teamRenames);

        // Must follow saveTeamsForGroup, which records the persisted ids of the associations' old names.
        const renames = this.teamSettingsList
            .filter(list => list.originalAssociation != null && list.originalAssociation !== list.association)
            .map(list => ({ from: list.originalAssociation, to: list.association }));
        this._projectModelService.renameAssociations(this.selectedGroupId, renames);

        const currentAssociationNames = new Set<string>();
        for (const list of this.teamSettingsList) {
            currentAssociationNames.add(list.association);
            this._projectModelService.saveAssociationContacts(this.selectedGroupId, list.association, list.contacts);
        }
        // Clean up contacts for associations that existed when the page loaded but were removed in this session.
        for (const associationName of this._originalAssociationNames.keys()) {
            if (!currentAssociationNames.has(associationName)) {
                this._projectModelService.removeAssociationContacts(this.selectedGroupId, associationName);
            }
        }
    }

    public get associationCount(): number {
        return this.teamSettingsList.length;
    }

    public get currentTeamCount(): number {
        return this.teamSettingsList.sum(t => t.teamCount);
    }

    public get maxTeamsCountReached(): boolean {
        return this.currentTeamCount >= 120;
    }

    private _updateInfoMessages(): void {
        this._clearMessages();
        if (this.maxTeamsCountReached) {
            this._addMessage(this._i18n.tr('ui:max_team_count_info'), MessageType.info);
        } else if (this.associationCount < 2) {
            this._addMessage(this._i18n.tr('ui:min_association_count_info'), MessageType.info);
        }
    }

    private _clearMessages(): void {
        this.messages.clear();
    }
    private _addMessage(message: string, type: MessageType): void {
        this.messages.add(new Message(message, type));
    }
}
